import { ImageOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBlob } from '../lib/api/client';
import { cn } from '../lib/utils';

/**
 * Image / video that fetches its source via the authenticated API client and
 * renders it through a blob URL. Needed because the admin app uses Bearer-token
 * auth (not cookies) and the browser's built-in `<img src>` / `<video src>`
 * loaders don't include Authorization headers.
 *
 * The blob URL is created on mount and revoked on unmount to avoid leaking
 * memory across review-dialog opens. Aborts an in-flight fetch if the path
 * changes mid-flight (rapid clicks across reports).
 */

interface AuthedImageProps {
  path: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
}

export function AuthedImage({ path, alt = '', className, onClick }: AuthedImageProps): JSX.Element {
  const url = useAuthedBlobUrl(path);
  if (url === 'loading') {
    return (
      <div
        className={cn(
          'flex animate-pulse items-center justify-center rounded-md bg-surface-tinted text-ink-subtle',
          className,
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (url === 'error') {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-md border border-line bg-surface-tinted text-ink-subtle',
          className,
        )}
        title="Could not load media"
      >
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className={className}
      onClick={onClick}
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  );
}

interface AuthedVideoProps {
  path: string;
  className?: string;
  controls?: boolean;
}

export function AuthedVideo({ path, className, controls = true }: AuthedVideoProps): JSX.Element {
  const url = useAuthedBlobUrl(path);
  if (url === 'loading') {
    return (
      <div
        className={cn(
          'flex aspect-video animate-pulse items-center justify-center rounded-md bg-surface-tinted text-ink-subtle',
          className,
        )}
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (url === 'error') {
    return (
      <div
        className={cn(
          'flex aspect-video items-center justify-center rounded-md border border-line bg-surface-tinted text-ink-subtle',
          className,
        )}
      >
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  return <video src={url} className={className} controls={controls} preload="metadata" />;
}

// ─── Hook ─────────────────────────────────────────────────────────────

type BlobUrlState = string | 'loading' | 'error';

function useAuthedBlobUrl(path: string): BlobUrlState {
  const [state, setState] = useState<BlobUrlState>('loading');

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    const controller = new AbortController();
    setState('loading');

    apiBlob(path, { signal: controller.signal })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setState(createdUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setState('error');
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path]);

  return state;
}
